import React, { useState } from 'react';
import { Calculator, FileText, TrendingUp, CheckCircle, Menu, X, Mail, Phone, MapPin } from 'lucide-react';

// Logo Component
const Logo = ({ size = 'default', className = '' }) => {
  const dimensions = size === 'large' ? 'w-16 h-16' : size === 'small' ? 'w-8 h-8' : 'w-10 h-10';
  
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <svg className={dimensions} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="48" fill="#2563EB" />
        <path d="M 30 25 L 30 75 L 35 75 L 35 52 L 55 75 L 62 75 L 42 52 L 62 25 L 55 25 L 35 48 L 35 25 Z" fill="white" />
        <line x1="65" y1="35" x2="75" y2="35" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <line x1="65" y1="42" x2="75" y2="42" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <line x1="65" y1="49" x2="75" y2="49" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <line x1="65" y1="56" x2="75" y2="56" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <line x1="65" y1="63" x2="75" y2="63" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
      {size !== 'small' && (
        <div className="flex flex-col">
          <span className="text-xl font-bold text-slate-800 leading-tight">Kerr</span>
          <span className="text-sm font-medium text-slate-600 leading-tight">Bookkeeping & Consulting</span>
        </div>
      )}
    </div>
  );
};

export default function KerrBookkeeping() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formElement = e.target;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('/', {
        method: 'POST',
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formDataToSend).toString()
      });
      
      if (response.ok) {
        setFormSubmitted(true);
        setTimeout(() => {
          setFormSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            businessName: '',
            message: ''
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');
    }
  };

  const services = [
    {
      icon: <Calculator className="w-8 h-8" />,
      title: "Bookkeeping Services",
      description: "Accurate financial record-keeping and transaction management to keep your business organized."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Financial Reporting",
      description: "Clear, comprehensive reports that give you insight into your business performance."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Business Consulting",
      description: "Strategic advice to help you make informed decisions and grow your business."
    }
  ];

  const benefits = [
    "Deep expertise and dedication to your success",
    "Personalized service tailored to your business needs",
    "Accurate and timely financial management",
    "Peace of mind knowing your finances are in expert hands"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Logo />
            
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              <a href="#services" className="text-slate-600 hover:text-slate-900 transition">Services</a>
              <a href="#about" className="text-slate-600 hover:text-slate-900 transition">About</a>
              <a href="#contact" className="text-slate-600 hover:text-slate-900 transition">Contact</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-slate-600"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-3 space-y-3">
              <a href="#services" className="block text-slate-600 hover:text-slate-900" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#about" className="block text-slate-600 hover:text-slate-900" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="#contact" className="block text-slate-600 hover:text-slate-900" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Your Financial Partner for Business Success
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Professional bookkeeping and consulting services to help your business thrive. Let us handle the numbers so you can focus on growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#contact" 
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition text-center"
            >
              Get Started
            </a>
            <a 
              href="#services" 
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold border-2 border-blue-600 hover:bg-blue-50 transition text-center"
            >
              Our Services
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive financial services designed to support your business at every stage
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition border border-slate-100"
              >
                <div className="text-blue-600 mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-12 text-center">
            Meet Jeremy Kerr
          </h2>
          
          <div className="grid md:grid-cols-5 gap-8 mb-12 items-start">
            <div className="md:col-span-2 flex justify-center">
              <img 
                src="/images/jeremy-headshot.jpg" 
                alt="Jeremy Kerr, Owner of Kerr Bookkeeping & Consulting"
                className="rounded-xl shadow-lg w-full max-w-sm object-cover"
              />
            </div>
            
            <div className="md:col-span-3 flex flex-col justify-center space-y-4">
              <p className="text-lg text-slate-700">
                With over 20 years of experience in financial management and accounting, Jeremy Kerr founded Kerr Bookkeeping & Consulting to help small businesses achieve the financial clarity they need to thrive. His passion lies in helping companies make the right financial decisions—because in small business, even minor changes can mean the difference between profitability and running in the red.
              </p>
              <p className="text-lg text-slate-700">
                During his 14 years as Chief Financial Officer, Jeremy managed revenue growth from $10M to $35M while overseeing complex financial operations including acquisitions and multi-state compliance reporting. The most valuable lesson from that experience? Sustainable growth is built on solid financial foundations—a principle he brings to every client relationship.
              </p>
              <p className="text-lg text-slate-700">
                Jeremy's approach is personalized and thorough. He starts with an in-depth consultation to understand where your business stands financially and where you want it to go, then tailors his expertise to fit your style and goals. With experience across multiple industries—from manufacturing to credit unions to moving companies—he brings versatile knowledge in job costing, budgeting, cash flow forecasting, and both accrual and cash-based accounting.
              </p>
              <p className="text-lg text-slate-700">
                When he's not helping businesses maximize their profits, Jeremy stays busy with his three young kids' sports schedules. In his rare free time, he and his wife enjoy playing pickleball, golf, and soccer.
              </p>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="border-t border-slate-200 pt-12">
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">
              Why Choose Kerr Bookkeeping & Consulting?
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white p-6 rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700">{benefit}</p>
                  </div>
                ))}
              </div>
              
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-8 rounded-xl text-white shadow-lg h-full flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">Let's Work Together</h3>
                <p className="mb-6 text-blue-50">
                  Whether you're a small business owner, entrepreneur, or growing company, Kerr Bookkeeping & Consulting has the expertise to help you succeed.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5" />
                    <span>Free initial consultation</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5" />
                    <span>Flexible service packages</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5" />
                    <span>Ongoing support and advice</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Get In Touch</h2>
            <p className="text-lg text-slate-600">
              Ready to take control of your business finances? Reach out today to schedule a consultation.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Contact Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Phone</p>
                    <a href="tel:+16164376852" className="text-slate-600 hover:text-blue-600 transition">
                      (616) 437-6852
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Email</p>
                    <a href="mailto:jeremy@kerrbookkeeping.com" className="text-slate-600 hover:text-blue-600 transition break-all">
                      jeremy@kerrbookkeeping.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Location</p>
                    <p className="text-slate-600">Grand Rapids, MI</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-8 border border-slate-100">
              {formSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
                  <p className="text-slate-600">Your message has been received. We'll get back to you shortly.</p>
                </div>
              ) : (
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h3>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label htmlFor="businessName" className="block text-sm font-semibold text-slate-700 mb-2">
                        Business Name
                      </label>
                      <input
                        type="text"
                        id="businessName"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition"
                        placeholder="Your Business LLC"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                      How can we help you? *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="5"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition resize-none"
                      placeholder="Tell us about your business and what services you're interested in..."
                    ></textarea>
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
                  >
                    <Mail className="w-5 h-5" />
                    Send Message
                  </button>

                  <p className="text-sm text-slate-500 text-center mt-4">
                    * Required fields
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center mb-6">
            <svg className="w-12 h-12 mb-3" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="48" fill="#3B82F6" />
              <path d="M 30 25 L 30 75 L 35 75 L 35 52 L 55 75 L 62 75 L 42 52 L 62 25 L 55 25 L 35 48 L 35 25 Z" fill="white" />
              <line x1="65" y1="35" x2="75" y2="35" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <line x1="65" y1="42" x2="75" y2="42" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <line x1="65" y1="49" x2="75" y2="49" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <line x1="65" y1="56" x2="75" y2="56" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <line x1="65" y1="63" x2="75" y2="63" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <h3 className="text-xl font-bold mb-1">Kerr Bookkeeping & Consulting</h3>
            <p className="text-slate-400 text-sm">Your trusted partner in financial success</p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400 mb-6">
            <a href="tel:+16164376852" className="hover:text-white transition flex items-center gap-2">
              <Phone size={14} />
              (616) 437-6852
            </a>
            <a href="mailto:jeremy@kerrbookkeeping.com" className="hover:text-white transition flex items-center gap-2">
              <Mail size={14} />
              jeremy@kerrbookkeeping.com
            </a>
            <span className="flex items-center gap-2">
              <MapPin size={14} />
              Grand Rapids, MI
            </span>
          </div>

          <div className="border-t border-slate-700 pt-6 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Kerr Bookkeeping & Consulting, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}